import { BurnRedeemOnChain } from '@/classes/burnRedeemExtensionCore';
import { BURN_FEE, MULTI_BURN_FEE } from '@/common/constants';
import { BigNumber } from '@ethersproject/bignumber';
import { Version } from '@manifoldxyz/typed-contracts/dist/contract-versions/ContractVersion';

export type NFTMetadata = {
  name: string;
  description: string;
  image: string;
  animation_url?: string;
};

export function isUnlimitedSupply(burn: BurnRedeemOnChain): boolean {
  return burn.totalSupply === 0;
}

export function abbreviate(str: string, keepLeft = 4, keepRight = 4): string {
  if (!str) return str;
  return str.substring(0, keepLeft) + '...' + str.substring(str.length - keepRight);
}

export function abbreviateAddress(address: string): string {
  return abbreviate(address, 6, 4);
}

export function ultraAbbreviateAddress(address: string): string {
  return abbreviate(address, 2, 4);
}

function escapeRegExp(str: string): string {
  return str.replace(/([.*+?^=!:${}()|\[\]\/\\])/g, '\\$1');
}

export const sleep = (ms: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

function replaceAll(str: string, find: string, replace: string): string {
  return str.replace(new RegExp(escapeRegExp(find), 'g'), replace);
}

export function convertSVGToDataURI(raw: string): string {
  let encoded = raw.replace(/\s+/g, ' ');

  // According to Taylor Hunt, lowercase gzips better ... my tiny test confirms this
  encoded = replaceAll(encoded, '%', '%25');
  encoded = replaceAll(encoded, '> <', '><'); // normalise spaces elements
  encoded = replaceAll(encoded, '; }', ';}'); // normalise spaces css
  encoded = replaceAll(encoded, '<', '%3c');
  encoded = replaceAll(encoded, '>', '%3e');
  encoded = replaceAll(encoded, '"', "'");
  encoded = replaceAll(encoded, '#', '%23'); // needed for ie and firefox
  encoded = replaceAll(encoded, '{', '%7b');
  encoded = replaceAll(encoded, '}', '%7d');
  encoded = replaceAll(encoded, '|', '%7c');
  encoded = replaceAll(encoded, '^', '%5e');
  encoded = replaceAll(encoded, '`', '%60');
  encoded = replaceAll(encoded, '@', '%40');

  // charset reportedly not needed ... I need to test before implementing
  return 'data:image/svg+xml;charset=UTF-8,' + encoded;
}

export const mediaIsLandscape = (src: string): boolean => {
  const img = new Image();
  img.src = src;
  const width = img.width;
  let height = img.height;
  height = height + height; // Double the height to get best

  if (width > height) {
    return true;
  }

  return false;
};

export function isJSON (str: string): boolean {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
}

/**
 * @param url a url that may or may not have a web3 protocol like ar:// or ipfs://
 * @returns url with web3 protocol converted to web2 protocol with https://
 */
export const convertPotentialWeb3ProtocolToWeb2 = (url: string, id?: BigNumber): string => {
  let optimizedUrl = url;

  if (optimizedUrl.includes('ipfs://ipfs/')) {
    optimizedUrl = optimizedUrl.replace('ipfs://ipfs/', 'https://ipfs.io/ipfs/');
  } else if (optimizedUrl.includes('ipfs://')) {
    optimizedUrl = optimizedUrl.replace('ipfs://', 'https://ipfs.io/ipfs/');
  } else if (optimizedUrl.includes('ar://')) {
    optimizedUrl = optimizedUrl.replace('ar://', 'https://arweave.net/');
  } else if (id
    && optimizedUrl.includes('api.opensea.io/api/v1/metadata/')
    && optimizedUrl.includes('{id}')
  ) {
    // OpenSea API URL utilizes EIP1155 swappable `{id}` within the URL.
    // Convert the id to a hexstring before replacing.
    let hexTokenId = id.toHexString();
    hexTokenId = hexTokenId.replace('0x', '');
    optimizedUrl = optimizedUrl.replace('{id}', hexTokenId);
  }

  return optimizedUrl;
};

export const getFees = (version: Version) => {
  if (version === 'v1.0') {
    return {
      single: BigNumber.from('0'),
      set: BigNumber.from('0'),
    };
  } else {
    return {
      single: BURN_FEE,
      set: MULTI_BURN_FEE,
    };
  }
};
